import React, { useEffect, useMemo, useState } from "react";
import { Cpu, Filter, MoreHorizontal, Search, Settings as Cog, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { usePopup } from "../context/PopupContext";
import { logError, logInfo } from "../utils/logger";
import { useUserRole } from "../hooks/useUserRole";
import {
  renameUserDevice,
  refreshUserDevicesMap,
  subscribeDeviceLive,
  subscribeUserDevices,
  unlinkUserDevice,
  parseDeviceLastSeen,
  resolveDeviceOnline,
} from "../services/deviceService";
import { userApiService } from "../services/userApiService";
import PumpControl from "../components/PumpControl";
import IrrigationScheduleEditor from "../components/IrrigationScheduleEditor";
import IrrigationAutoRuleEditor from "../components/IrrigationAutoRuleEditor";
import HubNodesPanel from "../components/HubNodesPanel";
import PumpConnectPrompt from "../components/PumpConnectPrompt";
import {
  deviceTypeLabel,
  deviceSupportsPump,
  devicePreviewSensors,
  inferDeviceType,
  resolvePumpConnected,
} from "../utils/deviceCapabilities";

function getLastSeenText(lastSeen, nowMs = Date.now()) {
  if (!lastSeen) return "No data yet";
  const diffSec = Math.floor((nowMs - lastSeen) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function DevicesPage() {
  const { user } = useAuth();
  const { effectiveRole } = useUserRole();
  const { showPopup } = usePopup();
  const [userDevices, setUserDevices] = useState({});
  const [deviceLive, setDeviceLive] = useState({});
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState("");
  const [editingName, setEditingName] = useState("");
  const canRenameDevice =
    effectiveRole === "owner" ||
    effectiveRole === "operator" ||
    effectiveRole === "admin" ||
    effectiveRole === "super_admin";
  const canUnlinkDevice =
    effectiveRole === "owner" || effectiveRole === "admin" || effectiveRole === "super_admin";
  const canControlPump =
    effectiveRole === "owner" ||
    effectiveRole === "operator" ||
    effectiveRole === "admin" ||
    effectiveRole === "super_admin";
  const [statusTick, setStatusTick] = useState(() => Date.now());

  const reloadDevices = async () => {
    try {
      const map = await refreshUserDevicesMap();
      setUserDevices(map);
    } catch {
      // poll will refresh
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setStatusTick(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const liveUnsubs = [];

    const unsubUserDevices = subscribeUserDevices(
      user.uid,
      (devices) => {
        if (cancelled) return;
        setUserDevices(devices);

        // Subscribe to every owned device + linked pot nodes (even if hidden as cards)
        const ids = new Set(Object.keys(devices));
        Object.values(devices).forEach((d) => {
          (d.linkedNodes || []).forEach((n) => {
            if (n?.nodeId) ids.add(n.nodeId);
          });
        });

        liveUnsubs.splice(0).forEach((fn) => {
          try {
            fn();
          } catch {
            /* ignore */
          }
        });

        ids.forEach((deviceId) => {
          const unsubLive = subscribeDeviceLive(deviceId, (liveSnap) => {
            if (cancelled) return;
            setDeviceLive((prev) => ({
              ...prev,
              [deviceId]: liveSnap || {},
            }));
          });
          liveUnsubs.push(unsubLive);
        });
      },
      (error) => {
        logError("DevicesPage", "Failed to load user devices", error, {
          uid: user.uid,
        });
        showPopup({
          type: "error",
          title: "Devices Load Failed",
          message: "Unable to load devices right now.",
        });
      }
    );

    return () => {
      cancelled = true;
      unsubUserDevices();
      liveUnsubs.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
    };
  }, [showPopup, user]);

  const rows = useMemo(() => {
    return Object.entries(userDevices)
      .map(([id, device]) => {
        const live = deviceLive[id] || {};
        const lastSeen = parseDeviceLastSeen(live);
        const isSimulator = !!device?.isSimulator;
        const deviceType = inferDeviceType(id, device?.deviceType);
        const pumpConnected = resolvePumpConnected({
          deviceId: id,
          deviceType,
          pumpConnected: device?.pumpConnected,
        });
        return {
          id,
          name: device?.name || id,
          isSimulator,
          deviceType,
          pumpConnected,
          hubId: device?.hubId || "",
          linkedNodes: device?.linkedNodes || [],
          lastSeen,
          status: isSimulator
            ? "simulator"
            : resolveDeviceOnline(live, isSimulator, statusTick)
              ? "online"
              : "offline",
          temperature: live?.Temperature,
          humidity: live?.Humidity,
          soilMoisture: live?.SoilMoisture,
        };
      })
      .filter((item) => {
        // Pot nodes linked to a hub only appear inside that hub card (not as their own row)
        if (item.deviceType === "node" && item.hubId) return false;
        const matchesStatus = statusFilter === "all" ? true : item.status === statusFilter;
        const q = searchText.trim().toLowerCase();
        const matchesSearch = !q
          ? true
          : item.id.toLowerCase().includes(q) || item.name.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deviceLive, searchText, statusFilter, userDevices, statusTick]);

  const beginRename = (device) => {
    if (!canRenameDevice) {
      toast.warning("Viewer role can only view device data.");
      return;
    }
    setEditingId(device.id);
    setEditingName(device.name);
  };

  const saveRename = async () => {
    if (!user || !editingId) return;
    if (!editingName.trim()) {
      showPopup({
        type: "warning",
        title: "Invalid Name",
        message: "Device name cannot be empty.",
      });
      return;
    }
    try {
      await renameUserDevice(user.uid, editingId, editingName);
      logInfo("DevicesPage", "Renamed device", { uid: user.uid, deviceId: editingId });
      showPopup({
        type: "success",
        title: "Device Updated",
        message: "Device name updated successfully.",
      });
      toast.success("Device renamed.");
      setEditingId("");
      setEditingName("");
      await reloadDevices();
    } catch (error) {
      logError("DevicesPage", "Failed to rename device", error, {
        uid: user.uid,
        deviceId: editingId,
      });
      showPopup({
        type: "error",
        title: "Rename Failed",
        message: "Unable to update device name right now.",
      });
      toast.error("Rename failed");
    }
  };

  const removeDeviceLink = async (deviceId) => {
    if (!user) return;
    if (!canUnlinkDevice) {
      toast.warning("Operator can update data but cannot remove links.");
      return;
    }
    try {
      await unlinkUserDevice(user.uid, deviceId);
      showPopup({
        type: "success",
        title: "Device Unlinked",
        message: "Device removed from your account.",
      });
      toast.success("Device unlinked.");
      await reloadDevices();
    } catch (error) {
      logError("DevicesPage", "Failed to unlink device", error, {
        uid: user?.uid || null,
        deviceId,
      });
      showPopup({
        type: "error",
        title: "Unlink Failed",
        message: "Unable to remove device link right now.",
      });
      toast.error("Unlink failed");
    }
  };

  const markPumpDisconnected = async (deviceId) => {
    try {
      await userApiService.updateDevice(deviceId, { pumpConnected: false });
      toast.success("Pump marked as disconnected");
      await reloadDevices();
    } catch (error) {
      toast.error(error?.message || "Update failed");
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="App"
        title="Devices."
        highlight="Control center."
        description="Search, filter, rename, unlink, and jump straight to settings or history."
      />

      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid gap-6 min-w-0 w-full box-border">
          <Card className="shadow-soft min-w-0 overflow-hidden w-full">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between min-w-0">
                <span className="flex items-center gap-2 min-w-0">
                  <Cpu className="h-5 w-5 shrink-0 text-accent" />
                  Device fleet
                </span>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  <Button
                    className="w-full sm:w-auto"
                    variant="outline"
                    onClick={() => (window.location.href = "/dashboard")}
                  >
                    Dashboard
                  </Button>
                  <Button className="w-full sm:w-auto" onClick={() => (window.location.href = "/settings")}>
                    Settings <Cog className="h-4 w-4" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-3 p-4 sm:p-6 pt-0 min-w-0">
              <div className="rounded-2xl border border-border bg-cream/50 px-4 py-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  className="w-full bg-transparent outline-none text-sm"
                  placeholder="Search by device name or id"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <div className="rounded-2xl border border-border bg-cream/50 px-4 py-3 flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  className="w-full bg-transparent outline-none text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All status</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>

              <div className="rounded-2xl border border-border bg-cream/50 px-4 py-3 flex items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>Total</span>
                <span className="font-medium text-foreground">{rows.length}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 min-w-0 w-full">
            {rows.map((d) => (
              <div
                key={d.id}
                className="rounded-3xl border border-border bg-card shadow-soft p-4 sm:p-6 min-w-0 w-full box-border overflow-hidden"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div>
                    <div className="font-display text-2xl">{d.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">ID: {d.id}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">
                        {deviceTypeLabel(d.deviceType)}
                      </span>
                      {d.status === "simulator" ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-secondary text-muted-foreground px-3 py-1 text-xs">
                          <Cpu className="h-4 w-4" /> Simulator
                        </span>
                      ) : d.status === "online" ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-success/15 text-success px-3 py-1 text-xs">
                          <Wifi className="h-4 w-4" /> Online
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 rounded-full bg-destructive/15 text-destructive px-3 py-1 text-xs">
                          <WifiOff className="h-4 w-4" /> Offline
                        </span>
                      )}
                      {!d.isSimulator && (
                        <span className="text-xs text-muted-foreground">
                          Last seen: {getLastSeenText(d.lastSeen, statusTick)}
                        </span>
                      )}
                      {d.deviceType === "node" && d.hubId && (
                        <span className="text-xs text-muted-foreground">Linked hub: {d.hubId}</span>
                      )}
                    </div>
                  </div>

                  {(() => {
                    const sens = devicePreviewSensors(d.deviceType);
                    const colCount = [sens.temp, sens.humidity, sens.soil].filter(Boolean).length;
                    if (!colCount) return null;
                    return (
                      <div
                        className={`grid gap-2 sm:gap-3 w-full md:w-[360px] min-w-0 ${
                          colCount === 1 ? "grid-cols-1" : colCount === 2 ? "grid-cols-2" : "grid-cols-3"
                        }`}
                      >
                        {sens.temp && (
                          <div className="rounded-2xl border border-border bg-cream/50 p-2 sm:p-3 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Temp</div>
                            <div className="mt-1 font-display text-xl sm:text-2xl text-foreground truncate">
                              {d.temperature ?? "—"}
                              {d.temperature !== undefined && d.temperature !== null ? (
                                <span className="text-sm text-muted-foreground">°C</span>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {sens.humidity && (
                          <div className="rounded-2xl border border-border bg-cream/50 p-2 sm:p-3 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Humidity</div>
                            <div className="mt-1 font-display text-xl sm:text-2xl text-foreground truncate">
                              {d.humidity ?? "—"}
                              {d.humidity !== undefined && d.humidity !== null ? (
                                <span className="text-sm text-muted-foreground">%</span>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {sens.soil && (
                          <div className="rounded-2xl border border-border bg-cream/50 p-2 sm:p-3 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Soil</div>
                            <div className="mt-1 font-display text-xl sm:text-2xl text-foreground truncate">
                              {d.soilMoisture ?? "—"}
                              {d.soilMoisture !== undefined && d.soilMoisture !== null ? (
                                <span className="text-sm text-muted-foreground">%</span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {!d.isSimulator && (
                  <div className="mt-5 space-y-4">
                    {d.deviceType === "hub" && (
                      <HubNodesPanel
                        hubId={d.id}
                        linkedNodes={d.linkedNodes}
                        deviceLive={deviceLive}
                        canEdit={canControlPump}
                        pumpConnected={d.pumpConnected}
                        onChanged={reloadDevices}
                      />
                    )}

                    {d.deviceType === "node" && (
                      <div className="rounded-2xl border border-border bg-cream/20 p-4 text-sm text-muted-foreground">
                        This is a <span className="font-medium text-foreground">pot node</span>. It has no
                        pump controls here. Link it under a Hub with{" "}
                        <span className="font-medium text-foreground">Add node</span>, then irrigate from
                        the hub.
                        {d.hubId ? (
                          <span className="mt-1 block">
                            Currently linked to{" "}
                            <span className="font-mono text-foreground">{d.hubId}</span>.
                          </span>
                        ) : (
                          <span className="mt-1 block">Not linked to a hub yet.</span>
                        )}
                      </div>
                    )}

                    {deviceSupportsPump(d.deviceType) && !d.pumpConnected && (
                      <PumpConnectPrompt
                        deviceId={d.id}
                        canEdit={canControlPump}
                        onChanged={reloadDevices}
                      />
                    )}

                    {deviceSupportsPump(d.deviceType) && d.pumpConnected && (
                      <>
                        <PumpControl
                          deviceId={d.id}
                          deviceLabel={d.name}
                          online={d.status === "online"}
                          canControl={canControlPump}
                          liveReading={{
                            temperature: d.temperature,
                            humidity: d.humidity,
                            soilMoisture: d.soilMoisture,
                          }}
                          nodes={(d.linkedNodes || []).map((n) => {
                            const live = deviceLive[n.nodeId] || {};
                            const nested = deviceLive[d.id]?.pots?.[n.nodeId] || {};
                            const lastSeenCandidates = [
                              Number(nested?.lastSeen) || 0,
                              Number(live?.meta?.lastSeen) || 0,
                              Number(live?.deviceStatus?.lastSeen) || 0,
                            ].filter((x) => x > 1e12);
                            const lastSeen = lastSeenCandidates.length
                              ? Math.max(...lastSeenCandidates)
                              : 0;
                            const potOnline = lastSeen > 0 && statusTick - lastSeen <= 90 * 1000;
                            const soilRaw = potOnline
                              ? (nested.SoilMoisture ?? live.SoilMoisture)
                              : null;
                            const soil = Number.isFinite(Number(soilRaw)) ? Number(soilRaw) : null;
                            return {
                              nodeId: n.nodeId,
                              potIndex: n.potIndex,
                              name: n.name || n.nodeId,
                              soil,
                              online: potOnline,
                              needsWater: soil != null && soil < 40,
                            };
                          })}
                        />
                        <IrrigationScheduleEditor
                          deviceId={d.id}
                          deviceLabel={d.name}
                          disabled={!canControlPump}
                        />
                        <IrrigationAutoRuleEditor
                          deviceId={d.id}
                          deviceLabel={d.name}
                          disabled={!canControlPump}
                        />
                        {canControlPump && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                            onClick={() => markPumpDisconnected(d.id)}
                          >
                            Mark pump as disconnected (hide controls)
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="mt-5 grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap">
                  <Button
                    className="w-full sm:w-auto"
                    size="sm"
                    variant="outline"
                    onClick={() => beginRename(d)}
                    disabled={!canRenameDevice}
                  >
                    Rename
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    size="sm"
                    variant="outline"
                    onClick={() => (window.location.href = "/settings")}
                  >
                    Settings <Cog className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    size="sm"
                    variant="outline"
                    onClick={() => (window.location.href = "/history")}
                  >
                    History
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    size="sm"
                    variant="destructive"
                    onClick={() => removeDeviceLink(d.id)}
                    disabled={!canUnlinkDevice}
                  >
                    Remove link
                  </Button>
                  <Button
                    className="w-full sm:w-auto col-span-2 sm:col-span-1"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      toast("More actions", {
                        description: "Camera setup, calibration, and exports can be added here next.",
                      })
                    }
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {!rows.length && (
              <div className="rounded-3xl border border-border bg-card p-10 shadow-soft text-center">
                <div className="font-display text-2xl">No devices found</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Try adjusting filters or add a device from the Dashboard.
                </p>
                <div className="mt-6">
                  <Button onClick={() => (window.location.href = "/dashboard")}>Go to Dashboard</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </PageSection>

      {editingId && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <div className="w-full max-w-lg rounded-3xl border border-border bg-card p-8 shadow-elevated">
            <div className="font-display text-2xl">Rename device</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Update display name for <span className="font-mono text-foreground">{editingId}</span>.
            </p>
            <input
              className="mt-6 w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="Device name"
            />
            <div className="mt-6 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditingId("")}>
                Cancel
              </Button>
              <Button onClick={saveRename}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
